import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, startGoogleLogin } from "../lib/auth";
import { LegalLinks } from "../components/Legal";
import { toast } from "sonner";
import { AUTH } from "../constants/testIds";
import { User, Mail, Lock, Eye, EyeOff } from "lucide-react";

// Login ekranıyla aynı marka dili (YZMatematik referans görselleri):
// koyu lacivert zemin, altın CTA, mavi vurgu.
export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Hesabın oluşturuldu!");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kayıt başarısız");
    } finally { setBusy(false); }
  };
  const google = async () => {
    try {
      await startGoogleLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google ile giriş başlatılamadı");
    }
  };

  return (
    <div className="min-h-screen relative overflow-y-auto">
      {/* watermark.png temiz öğrenci fotoğrafı — arka plan (metinsiz) */}
      <img src="/brand/watermark.png" alt="" aria-hidden="true"
           className="fixed inset-0 w-full h-full object-cover"
           style={{ objectPosition: "center 30%" }} />
      <div className="fixed inset-0"
           style={{ background: "linear-gradient(180deg, rgba(6,18,38,0.80) 0%, rgba(6,18,38,0.45) 40%, rgba(4,13,29,0.92) 100%)" }} />
      <div className="relative min-h-screen flex items-center justify-center p-6"
           style={{
             paddingTop: "max(2rem, env(safe-area-inset-top))",
             paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
           }}>
      <div className="w-full max-w-sm yz-enter">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/brand/yz-icon.png" alt="YZMatematik"
               className="h-20 w-20 rounded-full ring-2 ring-[#F5B301]/70 shadow-[0_0_40px_rgba(245,179,1,0.25)] mb-3" />
          <div className="font-display text-3xl tracking-tight">
            <span className="text-white">YZ</span> <span className="text-[#F5B301]">Matematik</span>
          </div>
          <div className="text-sm text-[#4DA3FF] mt-1">Yapay zekâ destekli dijital matematik koçu</div>
        </div>
        <div className="font-display text-3xl tracking-tight mb-1 text-white">Kayıt ol</div>
        <p className="text-sm text-white/60 mb-6">10 soruluk adaptif çalışmaya başla.</p>
        <button type="button" onClick={google} data-testid={AUTH.googleBtn}
          className="w-full mb-4 py-3 rounded-2xl bg-white text-[#061226] font-medium hover:bg-slate-100 transition-colors">
          Google ile kayıt ol
        </button>
        <div className="flex items-center gap-2 my-4 text-xs text-white/50">
          <span className="flex-1 h-px bg-white/15" /><span>veya</span><span className="flex-1 h-px bg-white/15" />
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="relative">
            <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid={AUTH.nameInput} required placeholder="Adın soyadın"
              value={name} onChange={(e) => setName(e.target.value)}
              className="w-full pl-10 pr-3 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 outline-none focus:border-[#4DA3FF] focus:ring-2 focus:ring-[#4DA3FF]/25 transition-shadow" />
          </div>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid={AUTH.emailInput} type="email" required placeholder="E-posta"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-3 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 outline-none focus:border-[#4DA3FF] focus:ring-2 focus:ring-[#4DA3FF]/25 transition-shadow" />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid={AUTH.passwordInput} type={showPw ? "text" : "password"} required minLength={6}
              placeholder="Şifre (en az 6)" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-11 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 outline-none focus:border-[#4DA3FF] focus:ring-2 focus:ring-[#4DA3FF]/25 transition-shadow" />
            <button type="button" onClick={() => setShowPw(!showPw)} data-testid="register-toggle-password"
              aria-label={showPw ? "Şifreyi gizle" : "Şifreyi göster"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button disabled={busy} data-testid={AUTH.registerBtn}
            className="w-full py-3 rounded-2xl font-semibold text-[#061226] disabled:opacity-60 transition-transform hover:-translate-y-px"
            style={{ background: "linear-gradient(180deg,#FFC93C 0%,#F5B301 100%)", boxShadow: "0 6px 18px rgba(245,179,1,0.35)" }}>
            {busy ? "Kaydediliyor…" : "Kayıt ol"}
          </button>
        </form>
        <div className="text-sm text-white/60 mt-5">
          Zaten üye misin?{" "}
          <Link data-testid={AUTH.toLogin} to="/giris" className="text-[#4DA3FF] hover:underline">Giriş yap</Link>
        </div>
        <LegalLinks dark className="mt-6" />
      </div>
      </div>
    </div>
  );
}
